/**
 * @fileoverview Report-Specific Payments Component
 *
 * This component displays payments that are specifically associated with a single Tier II report.
 * It shows payments embedded within the report data and provides report-context-specific functionality.
 *
 * Key Characteristics:
 * - Scope: Single Tier II report only
 * - Data Source: GET_TIERII_REPORT query (payments are embedded in report data)
 * - Grid Type: Standard DataGrid component
 * - Location: Used as a tab within report details (/staff/fulfillment/r/:reportId/payments)
 * - Features: Report workflow context, activity associations, report-specific actions
 * - Note: Reconciliation functionality is handled in GlobalPayments for comprehensive payment management
 *
 * This is distinct from the global staff payments view (GlobalPayments) which shows
 * all payments across the entire application with advanced filtering, bulk operations, and reconciliation.
 *
 * @see GlobalPayments - For app-wide payment management and reconciliation
 * @see PaymentDialog - Shared payment creation/editing form
 */

import Clear from "@mui/icons-material/Clear";
import Mode from "@mui/icons-material/Mode";
import { Button, Paper, Stack, Tooltip, Typography, useTheme, Grid } from "@mui/material";
import { GridActionsCellItem } from "@mui/x-data-grid-premium";
import { useAlerts } from "components/Alerts/AlertProvider";
import { ConfirmDialog } from "components/ConfirmDialog";
import { DataGrid } from "components/DataGrid";
import { getStaffFacingActivityTitle } from "encamp-shared/src/activity/activityDisplayNames";
import { getLobDashboardUrl } from "encamp-shared/src/constants/payments";
import { prettyPrintEnumValue } from "encamp-shared/src/utils/prettyPrintEnumValue";
import { ActivityStatus, Payment, PaymentStatus } from "generated-graphql/graphql";
import { useCancelPayment } from "hooks/usePayment";
import { useSelectedIdInUrl } from "hooks/useSelectedIdInUrl";
import { useCallback, useMemo, useState } from "react";
import { prettyPrintDateTime } from "util/dates";

import { useReportDetails } from "../../routes/Staff/Fulfillment/FulfillmentDetails/useReportDetails";
import { PaymentDialog } from "./PaymentDialog";

// Enhanced interface for reusability with cohort data
interface ReportPaymentsProps {
  // For cohort mode - provide data directly
  activities?: any[];
  tenantId?: string;
  cohortMode?: boolean;
  showPaper?: boolean;
  title?: string;
  // For fulfillment mode - use existing data hooks
  showActions?: boolean;
  showCreateButton?: boolean;
  // For cohort mode - auto-populate payment with these report IDs
  cohortMemberReportIds?: string[];
  // Tooltip to show when create button is disabled
  disabledButtonTooltip?: string;
}

function WorkflowPayments({
  activities: externalActivities,
  tenantId: externalTenantId,
  cohortMode = false,
  showPaper = true,
  title = "Payments",
  showActions = true,
  showCreateButton = true,
  cohortMemberReportIds,
  disabledButtonTooltip,
}: ReportPaymentsProps = {}) {
  const theme = useTheme();
  const alerts = useAlerts();
  const { data, loading, refetch } = useReportDetails();

  const { selectedId: paymentId, pushIdToUrl, popIdFromUrl } = useSelectedIdInUrl("paymentId");

  const [showCancelConfirmation, setShowCancelConfirmation] = useState(false);
  const [cancelPayment, setCancelPayment] = useState<Payment | undefined>(undefined);
  const [showPaymentForm, setShowPaymentForm] = useState(false);

  const closeModal = useCallback(() => {
    setShowCancelConfirmation(false);
    setCancelPayment(undefined);
    setShowPaymentForm(false);

    // Use the hook's popIdFromUrl instead of manual navigation
    if (paymentId) {
      popIdFromUrl();
    }
  }, [paymentId, popIdFromUrl]);

  const payments = useMemo(() => {
    if (cohortMode && externalActivities) {
      // Cohort mode: collect all payments from all activities
      return externalActivities.flatMap((activity) =>
        (activity.payments || []).map((payment: any) => ({
          ...payment,
          activity,
        }))
      );
    }

    // Fulfillment mode: use existing logic
    return (
      data?.tierIIReport.payments?.map((p: any) => ({
        ...p,
        activity: data.tierIIReport.currentWorkflow?.activities.find(
          (a: any) => a.id === p.activityId
        ),
      })) ?? []
    );
  }, [
    cohortMode,
    externalActivities,
    data?.tierIIReport.payments,
    data?.tierIIReport.currentWorkflow?.activities,
  ]);

  const [cancelPaymentMutation, { loading: deleting }] = useCancelPayment({
    refetchQueries: ["GetTierIIReport"],
  });

  const openPaymentsForm = (paymentId?: string) => {
    if (paymentId) {
      // Use the hook's pushIdToUrl instead of manual navigation
      pushIdToUrl(paymentId);
    } else {
      // For new payments, show the form without URL navigation
      setShowPaymentForm(true);
    }
  };

  const openCancelConfirmation = (payment: Payment) => {
    setCancelPayment(payment);
    setShowCancelConfirmation(true);
  };

  const handleCancelPayment = useCallback(async () => {
    if (!cancelPayment) return;
    try {
      await cancelPaymentMutation({ variables: { id: cancelPayment.id } });
      alerts.success("Successfully cancelled the payment");
    } catch (err) {
      alerts.error("There was an error cancelling this payment", err);
    }
    closeModal();
  }, [alerts, cancelPayment, cancelPaymentMutation, closeModal]);

  const lobDashboardUrl = useMemo(() => {
    if (!cancelPayment?.lobMail?.type) return undefined;
    return getLobDashboardUrl({
      paymentId: cancelPayment.id,
      lobMailType: cancelPayment.lobMail.type,
      env: import.meta.env.VITE_ENV,
    });
  }, [cancelPayment?.id, cancelPayment?.lobMail?.type]);

  const createPaymentButton = (showCreateButton || disabledButtonTooltip) && (
    <Grid size="auto">
      <Tooltip title={disabledButtonTooltip ?? ""} arrow>
        <span>
          <Button
            variant="contained"
            onClick={() => openPaymentsForm()}
            disabled={!showCreateButton}
          >
            Make or record a payment
          </Button>
        </span>
      </Tooltip>
    </Grid>
  );

  const content = (
    <Stack spacing={theme.spacing(2)}>
      {title ? (
        <Grid container spacing={2} alignItems="center" sx={{ mb: 2 }}>
          <Grid size="grow">
            <Typography variant={showPaper ? "h6" : "h5"}>{title}</Typography>
          </Grid>
          {createPaymentButton}
        </Grid>
      ) : (
        createPaymentButton && (
          <Grid container justifyContent="flex-end" sx={{ mb: 2 }}>
            {createPaymentButton}
          </Grid>
        )
      )}
      <DataGrid
        sx={{
          "& .MuiDataGrid-virtualScroller": {
            minHeight: "50px",
          },
        }}
        hideFooter
        loading={loading}
        rows={payments}
        disableRowSelectionOnClick
        onRowClick={({ row }) => openPaymentsForm(row.id)}
        initialState={{
          sorting: {
            sortModel: [{ field: "paidAt", sort: "desc" }],
          },
        }}
        columns={[
          {
            headerName: "Status",
            field: "status",
            flex: 1,
            renderCell: ({ row }) => prettyPrintEnumValue(row.status),
          },
          { headerName: "Payee", field: "payee", flex: 1 },
          { headerName: "Type", field: "payeeType", flex: 1 },
          {
            headerName: "Activity",
            field: "activity",
            valueGetter(value) {
              return value ? getStaffFacingActivityTitle(value) : undefined;
            },
            flex: 1,
          },
          {
            headerName: "Payment Date",
            field: "paidAt",
            flex: 1,
            renderCell(params) {
              return params.value ? prettyPrintDateTime(params.value) : "Not paid";
            },
          },
          {
            headerName: "Payment Method",
            field: "paymentMethod",
            flex: 1,
            renderCell(params) {
              return prettyPrintEnumValue(params.value);
            },
          },
          {
            headerName: "Amount",
            field: "amount",
            valueFormatter(value) {
              return new Intl.NumberFormat("en-US", {
                style: "currency",
                currency: "USD",
              }).format(value);
            },
            flex: 1,
          },
          ...(showActions
            ? [
                {
                  type: "actions" as const,
                  field: "actions",
                  align: "left" as const,
                  getActions({ row }: { row: any }) {
                    const actions = [
                      <GridActionsCellItem
                        key="edit"
                        label="Edit"
                        icon={<Mode />}
                        onClick={() => openPaymentsForm(row.id)}
                      />,
                    ];

                    if (row.status !== PaymentStatus.Cancelled) {
                      actions.push(
                        <GridActionsCellItem
                          key="cancel"
                          label="Cancel"
                          icon={<Clear />}
                          onClick={() => openCancelConfirmation(row as Payment)}
                        />
                      );
                    }

                    return actions;
                  },
                },
              ]
            : []),
        ]}
      />
    </Stack>
  );

  return (
    <>
      {showPaper ? (
        <Paper
          sx={{
            padding: theme.spacing(2),
          }}
          elevation={0}
        >
          {content}
        </Paper>
      ) : (
        content
      )}
      {(!!paymentId || showPaymentForm) && (cohortMode || data) && (
        <PaymentDialog
          open={!!paymentId || showPaymentForm}
          onClose={closeModal}
          tenantId={externalTenantId || data?.tierIIReport.tenant.id}
          facilityId={data?.tierIIReport.facility.id}
          reportId={data?.tierIIReport.id}
          paymentId={paymentId}
          activityId={
            cohortMode
              ? externalActivities?.find(
                  (a) =>
                    a.status === ActivityStatus.InProgress ||
                    a.status === ActivityStatus.StaffCompleted
                )?.id || externalActivities?.[0]?.id
              : data?.tierIIReport.currentWorkflow?.currentActivityId
          }
          associatedReportIds={cohortMode ? cohortMemberReportIds : []}
          onCompleted={() => {
            if (refetch) {
              refetch();
            }
          }}
        />
      )}
      {showCancelConfirmation && (
        <ConfirmDialog
          header="Are you sure you want to cancel this payment?"
          resources={[
            {
              resourceType: "Payment",
              resourceName: cancelPayment?.id,
            },
          ]}
          info={`This will cancel the payment and attempt to cancel the Lob check if one exists. If the check has already been mailed, it cannot be cancelled and may still be delivered.${
            lobDashboardUrl ? ` Lob URL: ${lobDashboardUrl}` : ""
          }`}
          confirmText="Yes, Cancel"
          cancelText="No, Keep Payment"
          open={showCancelConfirmation}
          onClose={closeModal}
          onConfirm={handleCancelPayment}
          loading={deleting}
        />
      )}
    </>
  );
}

// Backward compatibility wrapper for fulfillment
export function ReportPayments() {
  return <WorkflowPayments />;
}

// Export the enhanced component for cohort usage
export { WorkflowPayments };
